import { Box, Flex } from "@chakra-ui/react";
import { PEOPLE_TEAM_ROUTES, SHARED_ROUTES } from "definitions/constants/routeConstants";
import InnerSidebarNavButton from "layouts/Sidebar/InnerSidebarNavButton";
import { PropsWithChildren, useMemo } from "react";
import { generatePath } from "react-router";
import { useFeatureContext } from "contexts/FeatureContext";

type InnerSidebarRow = {
  label: string;
  route: string;
  featureFlag?: string;
};

// Define all sidebar items with their feature flags
const ALL_SIDEBAR_ITEMS: InnerSidebarRow[] = [
  { label: "Company Info", route: generatePath(PEOPLE_TEAM_ROUTES.company.info) },
  { label: "Email Settings", route: generatePath(PEOPLE_TEAM_ROUTES.company.emailSettings) },
  { label: "Calendar Settings", route: generatePath(PEOPLE_TEAM_ROUTES.company.calendarSettings) },
  { label: "SSO Settings", route: generatePath(PEOPLE_TEAM_ROUTES.company.ssoSettings) },
  { label: "Nudges Settings", route: generatePath(PEOPLE_TEAM_ROUTES.company.nudgesSettings), featureFlag: "nudges_system" },
  { label: "People Team", route: generatePath(PEOPLE_TEAM_ROUTES.company.peopleTeamSettings) },
  { label: "Notifications", route: generatePath(PEOPLE_TEAM_ROUTES.company.notifiers) },
  { label: "Task Settings", route: generatePath(PEOPLE_TEAM_ROUTES.company.taskSettings) },
  { label: "New Hire Experience", route: generatePath(PEOPLE_TEAM_ROUTES.company.newHireExperienceSettings) },
  { label: "Individual Account Settings", route: generatePath(SHARED_ROUTES.individualSettings) },
];

type InnerSidebarProps = PropsWithChildren<{
  sidebarList?: InnerSidebarRow[];
}>;

export default function InnerSidebar({ children, sidebarList }: InnerSidebarProps) {
  const { isEnabled } = useFeatureContext();

  // Filter sidebar items based on feature flags
  const filteredSidebarList = useMemo(() => {
    const itemsToFilter = sidebarList || ALL_SIDEBAR_ITEMS;

    return itemsToFilter.filter((item) => {
      // If no feature flag is required, always show the item
      if (!item.featureFlag) return true;

      // Check if the feature is enabled
      return isEnabled(item.featureFlag);
    });
  }, [sidebarList, isEnabled]); // isEnabled now properly updates when features change

  return (
    <Box
      display="flex"
      height="100%"
      py={4}
    >
      <Flex
        ml={-6}
        mt={-6}
        mr={6}
        p={6}
        pt={10}
        bg="bg.surface"
        borderRadius="md"
        boxShadow="md"
        w="200px"
        height="calc(100% + var(--chakra-sizes-12))"
        gap="1"
        direction="column"
      >
        {filteredSidebarList.map((item) => (
          <InnerSidebarNavButton key={item.route} route={item.route}>
            {item.label}
          </InnerSidebarNavButton>
        ))}
      </Flex>
      <Box width="100%" p={4}>
        {children}
      </Box>
    </Box>
  );
}
